import i18next from 'i18next';
import React from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import ReactDOM from 'react-dom';

type ConfirmActionProps = {
	title: string;
	message?: React.ReactNode;
	isLoading?: boolean;
	onConfirm?: () => void;
	onCancel?: () => void;
};

export const ConfirmActionContext = React.createContext<{
	confirmAction: (options: ConfirmActionProps) => Promise<boolean>;
	isConfirmShown?: boolean;
}>({} as any);

export const ConfirmActionProvider = (props: {
	children?: React.ReactNode;
}) => {
	const [isShown, setShown] = React.useState<boolean>(false);

	const callback = React.useRef<{
		props: ConfirmActionProps;
		resolve: (confirmed: boolean) => void;
	}>();

	const confirmAction = React.useCallback(
		(props: ConfirmActionProps) => {
			return new Promise<boolean>((resolve) => {
				callback.current = { resolve, props };
				setShown(true);
			});
		},
		[]
	);

	const setDisplayAndCall = (confirmed: boolean) => {
		setShown(false);
		if (callback.current) {
			callback.current.resolve(confirmed);
			callback.current = undefined;
		}
	};

	return (
		<ConfirmActionContext.Provider value={{ confirmAction, isConfirmShown: isShown }}>
			{props.children}
			{
				isShown
					? ReactDOM.createPortal(
						<Modal
							show={isShown}
							onExit={() => {
								callback.current?.props?.onCancel?.()
								setDisplayAndCall(false)
							}}
						>
							<Modal.Header>
								<Modal.Title>{callback.current?.props?.title}</Modal.Title>
							</Modal.Header>
							<Modal.Body>
								{callback.current?.props?.message}
							</Modal.Body>
							<Modal.Footer>
								<Button
									variant='link'
									onClick={
										() => {
											callback.current?.props?.onCancel?.()
											setDisplayAndCall(false)
										}
									}
									disabled={callback.current?.props.isLoading}
								>
									{i18next.t('cancel')}
								</Button>
								<Button
									onClick={
										async () => {
											await callback.current?.props?.onConfirm?.()
											setDisplayAndCall(true)
										}
									}
									disabled={callback.current?.props.isLoading}
								>
									{callback.current?.props.isLoading ? <Spinner animation='border' size='sm' /> : i18next.t('confirm')}
								</Button>
							</Modal.Footer>
						</Modal>,
						document.body
					)
					: null
			}
		</ConfirmActionContext.Provider>
	);
};

export const useConfirmAction = () => {
	return React.useContext(ConfirmActionContext);
};
